import * as React from "react";
import { Link, useParams } from "react-router-dom";
import {
    ActionButton,
    Card,
    Dialog,
    DialogContent,
} from "@jhool-io/fe-components";
import { useIsMutating } from "@tanstack/react-query";
import styles from "./AppointmentDetails.module.scss";

import {
    removeEnumUnderscore,
    getTimeDuration,
    formatDate,
    cn,
} from "../../../utils/helpers";
import { useFetchSessionNote } from "../../../hooks/queries/note";
import EditNoteAppointmentDetailsForm from "../../../modules/notes/components/EditNoteAppointmentDetailsForm/EditNoteAppointmentDetailsForm";
import { useAppSelector } from "../../../hooks/useRedux";
import EditIcon from "../../../icons/Edit";
import {
    ModifierTypes,
    NoteTypes,
} from "../../../modules/notes/types/notes.types";
import { getNoteTypeTextWithTag } from "../../../modules/notes/helpers/notes.helpers";

export default function AppointmentDetails() {
    const [showEditDialog, setShowEditDialog] = React.useState(false);

    const { practice } = useAppSelector((state) => state.userPractice);

    // Get id from url params
    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const { data, isSuccess } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    const modifiers = Object.values(ModifierTypes);

    const matchingModifiers = modifiers.filter((value) =>
        data?.data?.modifier_codes?.includes(value.split(" ")[0])
    );

    const isEditNoteMutating = useIsMutating(["edit-note"]);

    const getProviderWithRole = (roleType: "author" | "supervisor") => {
        const roleAvailable = data?.data.note_signatures?.find(
            (item) => item.role === roleType
        );

        return roleAvailable;
    };

    const handleShowEditDialog = () => {
        setShowEditDialog((state) => !state);
    };

    if (!data && !isSuccess) {
        return null;
    }

    return (
        <>
            <Dialog open={showEditDialog}>
                <DialogContent
                    title="Edit Appointment Details"
                    handleCloseDialog={handleShowEditDialog}
                    variant="center"
                    isSubmitBtnDisabled={isEditNoteMutating > 0}
                    isCancelBtnDisabled={isEditNoteMutating > 0}
                    submitBtnFormValue="edit-appointment-details"
                >
                    <EditNoteAppointmentDetailsForm
                        onFormSubmit={handleShowEditDialog}
                    />
                </DialogContent>
            </Dialog>
            <Card>
                <h2 className="uppercase text-base font-semibold flex items-center gap-x-1 mb-[--spacing-items]">
                    Appointment Details
                    <ActionButton
                        onClick={handleShowEditDialog}
                        variant="can-hover"
                        size="auto"
                        aria-label="edit-btn"
                        actionTitle="Edit appointment details"
                    >
                        <EditIcon />
                    </ActionButton>
                </h2>
                <div
                    className={cn(
                        "grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-[--spacing-items]"
                    )}
                >
                    <div>
                        <p className="section-info">
                            {practice?.is_supervisor &&
                            data.data.type === NoteTypes.SUPERVISION_NOTE
                                ? "Supervisee’s client"
                                : "Client name"}
                        </p>
                        <Link
                            to={`/clients/${data.data.client.client_id}`}
                            className="section-value capitalize underline !text-textLink"
                        >
                            {`${data.data.client.first_name} ${data.data.client.last_name}`}
                        </Link>
                    </div>
                    <div>
                        <p className="section-info">Date of service</p>
                        <p className="section-value">
                            {data.data.date_of_service
                                ? formatDate(
                                      data.data.date_of_service as string
                                  )
                                : "--"}
                        </p>
                    </div>
                    <div>
                        <p className="section-info">Time</p>
                        <p className="section-value">
                            {`${formatDate(
                                data.data.session_start_time as string,
                                false,
                                "h:mmaaa"
                            )} - ${formatDate(
                                data.data.session_end_time as string,
                                false,
                                "h:mmaaa"
                            )}`}
                        </p>
                    </div>
                    <div>
                        <p className="section-info">Duration</p>
                        <p className="section-value">
                            {getTimeDuration(
                                formatDate(
                                    data.data.session_start_time || "",
                                    false,
                                    "HH:mm"
                                ),
                                formatDate(
                                    data.data.session_end_time || "",
                                    false,
                                    "HH:mm"
                                )
                            )}
                        </p>
                    </div>
                    <div>
                        <p className="section-info">Appointment type</p>
                        <p className="section-value capitalize">
                            {removeEnumUnderscore(
                                data.data.appointment_type || ""
                            )}
                        </p>
                    </div>
                    <div>
                        <p className="section-info">Provider name</p>
                        <p className="section-value">
                            {`${data.data.provider.first_name} ${data.data.provider.last_name}`}
                        </p>
                    </div>
                    {getProviderWithRole("author") ? (
                        <div>
                            <p className="section-info">Provider signature</p>
                            <p className="section-value">
                                <span
                                    style={{
                                        display: "inline-block",
                                        marginRight: "3px",
                                    }}
                                >
                                    {getProviderWithRole("author")?.signature}
                                </span>
                                (
                                {formatDate(
                                    getProviderWithRole("author")
                                        ?.signature_date_time as string
                                )}
                                ,
                                <span
                                    className={styles.time}
                                    style={{
                                        display: "inline-block",
                                        marginLeft: "3px",
                                    }}
                                >
                                    {formatDate(
                                        getProviderWithRole("author")
                                            ?.signature_date_time as string,
                                        false,
                                        "h:mm a"
                                    )}
                                </span>
                                )
                            </p>
                        </div>
                    ) : null}
                    {getProviderWithRole("supervisor") ? (
                        <div>
                            <p className="section-info">Supervisor name</p>
                            <p className="section-value">
                                {getProviderWithRole("supervisor")?.first_name
                                    ? `${
                                          getProviderWithRole("supervisor")
                                              ?.first_name
                                      } ${
                                          getProviderWithRole("supervisor")
                                              ?.last_name
                                      }`
                                    : "--"}
                            </p>
                        </div>
                    ) : null}

                    {getProviderWithRole("supervisor")?.signature ? (
                        <div>
                            <p className="section-info">Supervisor signature</p>
                            <p className="section-value">
                                <span
                                    style={{
                                        display: "inline-block",
                                        marginRight: "3px",
                                    }}
                                >
                                    {
                                        getProviderWithRole("supervisor")
                                            ?.signature
                                    }
                                </span>
                                (
                                {formatDate(
                                    getProviderWithRole("supervisor")
                                        ?.signature_date_time as string
                                )}
                                ,
                                <span
                                    className={styles.time}
                                    style={{
                                        display: "inline-block",
                                        marginLeft: "3px",
                                    }}
                                >
                                    {formatDate(
                                        getProviderWithRole("supervisor")
                                            ?.signature_date_time as string,
                                        false,
                                        "h:mm a"
                                    )}
                                </span>
                                )
                            </p>
                        </div>
                    ) : null}
                    {data.data.modifier_codes?.length !== 0 &&
                        matchingModifiers.map((code, i) => (
                            <div key={code}>
                                <p className="section-info">
                                    {`Modifier ${i + 1}`}
                                </p>
                                <p className="section-value">{code}</p>
                            </div>
                        ))}

                    {data.data.type && (
                        <div className="col-span-2">
                            <p className="section-info">Note type</p>
                            <p className="section-value">
                                {getNoteTypeTextWithTag(data.data.type)}
                            </p>
                        </div>
                    )}
                </div>
            </Card>
        </>
    );
}
